import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import EcoIcon, { EcoIconName } from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import { useGoBack } from "../../../hooks/useGoBack";
import { isAdminFromToken } from "../../../utils/authToken";

import { getSummaryApi } from "../api/financeiroApi";
import type { FinDueSlot, FinSummary } from "../api/finTypes";
import {
  FIN_CASHFLOW_PAGE,
  FIN_DRE_PAGE,
  FIN_ENTRY_NEW_PAGE,
  FIN_PAYABLES_PAGE,
  FIN_RECEIVABLES_PAGE,
  FIN_SETTINGS_TAB_PAGE,
} from "../routes";
import { formatCurrency } from "../../dp/utils/format";
import { useFinPermissions } from "../utils/fin";
import "../../dp/pages/DpEco.css";
import "./Financeiro.css";

type ModuleCard = { title: string; description: string; icon: EcoIconName; to: string };

const SECTIONS: { id: string; title: string; modules: ModuleCard[] }[] = [
  {
    id: "lancamentos",
    title: "Lançamentos",
    modules: [
      {
        title: "Contas a pagar",
        description: "Despesas por vencimento, com baixa total ou parcial e comprovante.",
        icon: "arrowUp",
        to: FIN_PAYABLES_PAGE(),
      },
      {
        title: "Contas a receber",
        description: "Receitas por vencimento e o que já entrou.",
        icon: "arrowDown",
        to: FIN_RECEIVABLES_PAGE(),
      },
      {
        title: "Fluxo de caixa",
        description: "Realizado e previsto por dia ou mês, com o saldo projetado.",
        icon: "barChart",
        to: FIN_CASHFLOW_PAGE(),
      },
      {
        title: "DRE por centro de custo",
        description: "Receitas, despesas e resultado por grupo, subgrupo e centro de custo.",
        icon: "pieChart",
        to: FIN_DRE_PAGE(),
      },
    ],
  },
  {
    id: "cadastros",
    title: "Cadastros",
    modules: [
      {
        title: "Contas bancárias",
        description: "Bancos e caixa, com saldo inicial e saldo atual.",
        icon: "creditCard",
        to: FIN_SETTINGS_TAB_PAGE("contas"),
      },
      {
        title: "Plano de contas",
        description: "Categorias de receita e despesa em grupos.",
        icon: "layers",
        to: FIN_SETTINGS_TAB_PAGE("categorias"),
      },
      {
        title: "Fornecedores e clientes",
        description: "Quem você paga e de quem recebe, com CPF/CNPJ e PIX.",
        icon: "users",
        to: FIN_SETTINGS_TAB_PAGE("contrapartes"),
      },
      {
        title: "Repetições",
        description: "Contas que se repetem: aluguel, mensalidades, assinaturas.",
        icon: "calendar",
        to: FIN_SETTINGS_TAB_PAGE("repeticoes"),
      },
    ],
  },
];

const countLabel = (slot: FinDueSlot) => `${slot.count} ${slot.count === 1 ? "título" : "títulos"}`;

export default function FinanceiroPage() {
  const navigate = useNavigate();
  const { token, canWrite } = useFinPermissions();
  // Para quem tem o Financeiro como tela inicial, não há para onde voltar.
  const isAdmin = useMemo(() => isAdminFromToken(token), [token]);
  const goBack = useGoBack("/");

  const [summary, setSummary] = useState<FinSummary | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!token) return;
    getSummaryApi(token)
      .then(setSummary)
      .catch(() => setFailed(true));
  }, [token]);

  const payables = summary?.payables;
  const receivables = summary?.receivables;

  return (
    <div className="dpePage">
      <main className="dpePage__main">
        <EcoPageHeader
          crumbs={[{ label: "Início", onClick: () => navigate("/") }, { label: "Financeiro" }]}
          title="Financeiro"
          subtitle="Contas a pagar e a receber, saldos e fluxo de caixa da empresa."
          onBack={isAdmin ? goBack : undefined}
          backLabel="Voltar ao início"
          actions={
            canWrite ? (
              <>
                <button type="button" className="ecoBtn" onClick={() => navigate(FIN_ENTRY_NEW_PAGE("RECEBER"))}>
                  <EcoIcon name="plus" size={18} />
                  Conta a receber
                </button>
                <button
                  type="button"
                  className="ecoBtn ecoBtn--primary"
                  onClick={() => navigate(FIN_ENTRY_NEW_PAGE("PAGAR"))}
                >
                  <EcoIcon name="plus" size={18} />
                  Conta a pagar
                </button>
              </>
            ) : (
              <span className="ecoPill ecoPill--off">Somente leitura</span>
            )
          }
        />

        <section className="dpeSection" aria-labelledby="fin-hoje">
          <h2 id="fin-hoje" className="dpeSection__title">Hoje</h2>
          {failed ? (
            <div className="dpeAlert dpeAlert--calm">
              <span className="dpeAlert__text">
                <span className="dpeAlert__title">Não foi possível carregar o resumo.</span>
                <span className="dpeAlert__desc">As telas abaixo continuam disponíveis.</span>
              </span>
            </div>
          ) : !summary || !payables || !receivables ? (
            <div className="dpeAlert dpeAlert--calm">
              <span className="dpeAlert__text">
                <span className="dpeAlert__title">Carregando...</span>
              </span>
            </div>
          ) : (
            <div className="finSummary">
              <div className="finStat">
                <span className="finStat__label">Saldo em contas</span>
                <span className={`finStat__value ${Number(summary.totalBalance) < 0 ? "finMoney--negative" : ""}`}>
                  {formatCurrency(summary.totalBalance)}
                </span>
                <span className="finStat__hint">
                  {summary.accounts.length === 0
                    ? "Nenhuma conta cadastrada"
                    : `${summary.accounts.length} ${summary.accounts.length === 1 ? "conta" : "contas"}`}
                </span>
              </div>
              <button
                type="button"
                className={`finStat ${payables.overdue.count > 0 ? "finStat--bad" : ""}`}
                onClick={() => navigate(`${FIN_PAYABLES_PAGE()}?situacao=atrasados&periodo=todos`)}
              >
                <span className="finStat__label">A pagar atrasado</span>
                <span className="finStat__value">{formatCurrency(payables.overdue.amount)}</span>
                <span className="finStat__hint">{countLabel(payables.overdue)}</span>
              </button>
              <button
                type="button"
                className={`finStat ${payables.today.count > 0 ? "finStat--warn" : ""}`}
                onClick={() => navigate(`${FIN_PAYABLES_PAGE()}?periodo=hoje`)}
              >
                <span className="finStat__label">A pagar hoje</span>
                <span className="finStat__value">{formatCurrency(payables.today.amount)}</span>
                <span className="finStat__hint">{countLabel(payables.today)}</span>
              </button>
              <button
                type="button"
                className="finStat"
                onClick={() => navigate(`${FIN_PAYABLES_PAGE()}?periodo=7dias`)}
              >
                <span className="finStat__label">A pagar em 7 dias</span>
                <span className="finStat__value">{formatCurrency(payables.next7.amount)}</span>
                <span className="finStat__hint">{countLabel(payables.next7)}</span>
              </button>
              <button
                type="button"
                className={`finStat ${receivables.overdue.count > 0 ? "finStat--warn" : ""}`}
                onClick={() => navigate(`${FIN_RECEIVABLES_PAGE()}?situacao=atrasados&periodo=todos`)}
              >
                <span className="finStat__label">A receber atrasado</span>
                <span className="finStat__value">{formatCurrency(receivables.overdue.amount)}</span>
                <span className="finStat__hint">{countLabel(receivables.overdue)}</span>
              </button>
              <button
                type="button"
                className="finStat"
                onClick={() => navigate(`${FIN_RECEIVABLES_PAGE()}?periodo=7dias`)}
              >
                <span className="finStat__label">A receber em 7 dias</span>
                <span className="finStat__value">
                  {formatCurrency(Number(receivables.today.amount) + Number(receivables.next7.amount))}
                </span>
                <span className="finStat__hint">
                  {countLabel({
                    count: receivables.today.count + receivables.next7.count,
                    amount: "0",
                  })}
                </span>
              </button>
            </div>
          )}

          {summary?.unclassified && summary.unclassified.count > 0 ? (
            <div className="dpeAlert dpeAlert--calm finUnclassified">
              <span className="dpeAlert__text">
                <span className="dpeAlert__title">
                  {summary.unclassified.count} {summary.unclassified.count === 1 ? "lançamento" : "lançamentos"} sem centro de
                  custo ({formatCurrency(summary.unclassified.amount)}) nos últimos 12 meses
                </span>
                <span className="dpeAlert__desc">Sem centro de custo, o valor aparece à parte na DRE.</span>
              </span>
              <button
                type="button"
                className="ecoBtn ecoBtn--sm"
                onClick={() => navigate(`${FIN_PAYABLES_PAGE()}?centro=sem&periodo=todos&situacao=todos`)}
              >
                Classificar
              </button>
            </div>
          ) : null}

          {summary && summary.accounts.length > 0 ? (
            <div className="finTotals">
              {summary.accounts.map((a) => (
                <span key={a.id}>
                  {a.name}:{" "}
                  <strong className={Number(a.currentBalance) < 0 ? "finMoney--negative" : ""}>
                    {formatCurrency(a.currentBalance)}
                  </strong>
                </span>
              ))}
            </div>
          ) : null}
        </section>

        {SECTIONS.map((section) => (
          <section key={section.id} className="dpeSection" aria-labelledby={`fin-${section.id}`}>
            <h2 id={`fin-${section.id}`} className="dpeSection__title">
              {section.title}
            </h2>
            <div className="dpeModules">
              {section.modules.map((m) => (
                <button key={m.title} type="button" className="dpeModule" onClick={() => navigate(m.to)}>
                  <span className="dpeModule__icon">
                    <EcoIcon name={m.icon} size={20} strokeWidth={1.8} />
                  </span>
                  <span className="dpeModule__text">
                    <span className="dpeModule__title">{m.title}</span>
                    <span className="dpeModule__desc">{m.description}</span>
                  </span>
                  <EcoIcon name="chevronRight" className="dpeModule__chevron" />
                </button>
              ))}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
}
